import React, { useState, useCallback, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { X, Plus, Shuffle, Trash2, Upload, ChevronDown, ChevronUp, Eye, EyeOff, ToggleLeft, ToggleRight, List } from 'lucide-react';

const EntryList = ({ 
  entries, 
  onEntriesChange, 
  maxEntries = 100, 
  variant = 'main', 
  showWeights = false,
  isHidden = false,
  onToggleHidden
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(showWeights);
  const inputRef = useRef(null);
  const { t } = useLanguage();

  const addEntry = useCallback((text) => {
    const trimmed = text.trim();
    if (trimmed && entries.length < maxEntries) {
      const newEntry = {
        id: `entry-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        text: trimmed,
        weight: 1,
        enabled: true // New: toggle state
      };
      onEntriesChange([...entries, newEntry]);
    }
  }, [entries, onEntriesChange, maxEntries]);

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      addEntry(inputValue);
      setInputValue('');
    }
  };

  const handleAddClick = () => {
    if (inputValue.trim()) {
      addEntry(inputValue);
      setInputValue('');
      inputRef.current?.focus();
    }
  };

  const removeEntry = useCallback((id) => {
    onEntriesChange(entries.filter(e => e.id !== id));
  }, [entries, onEntriesChange]);

  // Toggle entry enabled/disabled
  const toggleEntry = useCallback((id) => {
    onEntriesChange(entries.map(e => 
      e.id === id ? { ...e, enabled: e.enabled === false ? true : false } : e
    ));
  }, [entries, onEntriesChange]);

  // Enable/disable all entries
  const toggleAllEntries = useCallback((enabled) => {
    onEntriesChange(entries.map(e => ({ ...e, enabled })));
  }, [entries, onEntriesChange]);

  // Update weight for an entry
  const updateWeight = useCallback((id, newWeight) => {
    onEntriesChange(entries.map(e => 
      e.id === id ? { ...e, weight: Math.max(1, Math.min(10, newWeight)) } : e
    ));
  }, [entries, onEntriesChange]);

  // Count enabled entries
  const enabledCount = entries.filter(e => e.enabled !== false).length;

  // Reset all weights to 1
  const resetWeights = useCallback(() => {
    onEntriesChange(entries.map(e => ({ ...e, weight: 1 })));
  }, [entries, onEntriesChange]);

  const clearAll = useCallback(() => {
    onEntriesChange([]);
  }, [onEntriesChange]);

  const shuffleEntries = useCallback(() => {
    const shuffled = [...entries];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    onEntriesChange(shuffled);
  }, [entries, onEntriesChange]);

  // Handle paste in input - auto bulk import
  const handlePaste = useCallback((e) => {
    const pastedText = e.clipboardData.getData('text');
    const lines = pastedText.split(/\n|,/).map(s => s.trim()).filter(Boolean);
    
    if (lines.length > 1) {
      e.preventDefault();
      const newEntries = lines.slice(0, maxEntries - entries.length).map(text => ({
        id: `entry-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        text,
        weight: 1
      }));
      onEntriesChange([...entries, ...newEntries]);
      setInputValue('');
    }
  }, [entries, onEntriesChange, maxEntries]);

  // Bulk import handler
  const handleBulkImport = useCallback(() => {
    const lines = bulkText.split(/\n/).map(s => s.trim()).filter(Boolean);
    if (lines.length > 0) {
      const newEntries = lines.slice(0, maxEntries - entries.length).map(text => ({
        id: `entry-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        text,
        weight: 1,
        enabled: true
      }));
      onEntriesChange([...entries, ...newEntries]);
      setBulkText('');
      setShowBulkImport(false);
    }
  }, [bulkText, entries, onEntriesChange, maxEntries]);

  // Replace all with bulk import
  const handleBulkReplace = useCallback(() => {
    const lines = bulkText.split(/\n/).map(s => s.trim()).filter(Boolean);
    if (lines.length > 0) {
      const newEntries = lines.slice(0, maxEntries).map(text => ({
        id: `entry-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        text,
        weight: 1,
        enabled: true
      }));
      onEntriesChange(newEntries);
      setBulkText('');
      setShowBulkImport(false);
    }
  }, [bulkText, onEntriesChange, maxEntries]);

  const bulkLineCount = bulkText.split(/\n/).filter(s => s.trim()).length;
  const disabledCount = entries.length - enabledCount;

  return (
    <div className={`entry-list ${isHidden ? 'hidden-mode' : ''}`} data-testid="entry-list">
      {/* Hidden mode overlay */}
      {isHidden && (
        <div className="hidden-overlay" onClick={onToggleHidden}>
          <EyeOff size={32} />
          <span>{t('wheel.surpriseModeTitle')}</span>
          <small>{t('wheel.clickToReveal')}</small>
        </div>
      )}

      <div className="entry-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleInputKeyDown}
          onPaste={handlePaste}
          placeholder={t('wheel.addEntryPlaceholder')}
          className="entry-input"
          aria-label={t('wheel.addEntry')}
          data-testid="entry-input"
          maxLength={100}
        />
        <button
          onClick={handleAddClick}
          className="btn-add"
          aria-label={t('wheel.addEntry')}
          disabled={!inputValue.trim() || entries.length >= maxEntries}
          data-testid="add-entry-button"
        >
          <Plus size={20} />
        </button>
      </div>

      <div className="entry-actions-container">
        <div className="entry-count-row">
          <span className="entry-count">
            {enabledCount}/{entries.length} {t('wheel.entries')}
            {disabledCount > 0 && <small className="disabled-count">({disabledCount} off)</small>}
          </span>
          {/* Hide/Show List Toggle */}
          <button
            onClick={onToggleHidden}
            className={`btn-action-sm ${isHidden ? 'active' : ''}`}
            aria-label={isHidden ? t('wheel.showEntries') : t('wheel.hideEntries')}
            title={isHidden ? t('wheel.showEntries') : t('wheel.hideEntries')}
            data-testid="toggle-hidden-button"
          >
            {isHidden ? <Eye size={16} /> : <EyeOff size={16} />}
            <span>{t('wheel.surprise')}</span>
          </button>
        </div>
        <div className="action-buttons-row">
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={`btn-action ${showAdvanced ? 'active' : ''}`}
            aria-label={t('wheel.weights')}
            title={t('wheel.weight')}
            data-testid="toggle-weights-button"
          >
            {showAdvanced ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            <span>{t('wheel.weights')}</span>
          </button>
          <button
            onClick={() => setShowBulkImport(true)}
            className="btn-action"
            aria-label={t('wheel.bulkImport')}
            data-testid="bulk-import-button"
          >
            <Upload size={16} />
            <span>{t('wheel.bulk')}</span>
          </button>
          <button
            onClick={shuffleEntries}
            className="btn-action"
            disabled={entries.length < 2}
            aria-label={t('wheel.shuffle')}
            data-testid="shuffle-button"
          >
            <Shuffle size={16} />
            <span>{t('wheel.shuffle')}</span>
          </button>
          <button
            onClick={clearAll}
            className="btn-action btn-danger"
            disabled={entries.length === 0}
            aria-label={t('wheel.clear')}
            data-testid="clear-button"
          >
            <Trash2 size={16} />
            <span>{t('wheel.clear')}</span>
          </button>
        </div>
      </div>

      {/* Weight info banner */}
      {showAdvanced && entries.length > 0 && (
        <div className="weight-info-banner">
          <span>⚖️ {t('wheel.weight')}</span>
          <div className="weight-actions">
            <button 
              onClick={() => toggleAllEntries(true)}
              className="btn-reset-weights"
              title={t('wheel.enabled')}
            >
              {t('wheel.enabled')}
            </button>
            <button 
              onClick={() => toggleAllEntries(false)}
              className="btn-reset-weights"
              title={t('wheel.disabled')}
            >
              {t('wheel.disabled')}
            </button>
            <button 
              onClick={resetWeights} 
              className="btn-reset-weights"
              data-testid="reset-weights-button"
            >
              {t('wheel.resetWeights')}
            </button>
          </div>
        </div>
      )}

      <ul className="entries-list" role="list" aria-label={t('wheel.entries')}>
        {entries.map((entry, index) => (
          <li 
            key={entry.id} 
            className={`entry-item ${showAdvanced ? 'with-weight' : ''} ${entry.enabled === false ? 'disabled' : ''}`} 
            data-testid={`entry-item-${index}`}
          >
            {/* Toggle checkbox */}
            <button
              onClick={() => toggleEntry(entry.id)}
              className={`entry-toggle ${entry.enabled === false ? 'off' : 'on'}`}
              aria-label={entry.enabled === false ? "Enable entry" : "Disable entry"}
              title={entry.enabled === false ? "Click to enable" : "Click to disable"}
            >
              {entry.enabled === false ? <ToggleLeft size={18} /> : <ToggleRight size={18} />}
            </button>
            
            <span 
              className="entry-color" 
              style={{ backgroundColor: entry.enabled === false ? '#ccc' : entry.color }}
              aria-hidden="true"
            />
            <span className="entry-text">{entry.text}</span>
            
            {/* Weight control */}
            {showAdvanced && (
              <div className="entry-weight-control">
                <button 
                  onClick={() => updateWeight(entry.id, entry.weight - 1)}
                  className="btn-weight"
                  disabled={entry.weight <= 1}
                  aria-label="Decrease weight"
                >
                  −
                </button>
                <span className="weight-value" title={`Weight: ${entry.weight}x`}>
                  {entry.weight}x
                </span>
                <button 
                  onClick={() => updateWeight(entry.id, entry.weight + 1)}
                  className="btn-weight"
                  disabled={entry.weight >= 10}
                  aria-label="Increase weight"
                >
                  +
                </button>
              </div>
            )}
            
            <button
              onClick={() => removeEntry(entry.id)}
              className="btn-remove"
              aria-label={`${t('wheel.removeEntry')} ${entry.text}`}
              data-testid={`remove-entry-${index}`}
            >
              <X size={16} />
            </button>
          </li>
        ))}
      </ul>

      {entries.length === 0 && (
        <p className="no-entries" data-testid="no-entries-message">
          {t('wheel.noEntries')}
        </p>
      )}

      {/* Bulk Import Modal */}
      {showBulkImport && (
        <div className="bulk-import-overlay" onClick={() => setShowBulkImport(false)}>
          <div className="bulk-import-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bulk-import-header">
              <h3>
                <List size={20} />
                {t('wheel.bulkImport')}
              </h3>
              <button
                onClick={() => setShowBulkImport(false)}
                className="bulk-import-close"
                aria-label={t('wheel.close')}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="bulk-import-content">
              <p className="bulk-import-hint">
                {t('wheel.bulkPlaceholder')}
              </p>
              <textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder="John&#10;Jane&#10;Alice&#10;Bob&#10;Charlie"
                className="bulk-import-textarea"
                rows={8}
                autoFocus
                data-testid="bulk-import-textarea"
              />
              <div className="bulk-import-info">
                {bulkLineCount > 0 && (
                  <span className="bulk-count">{t('wheel.itemsDetected', { count: bulkLineCount })}</span>
                )}
                <span className="bulk-limit">Max: {maxEntries}</span>
              </div>
            </div>
            
            <div className="bulk-import-actions">
              <button
                onClick={handleBulkImport}
                disabled={bulkLineCount === 0}
                className="btn-bulk-action btn-primary"
                data-testid="bulk-add-button"
              >
                <Plus size={18} />
                {t('wheel.bulkImportAdd')} ({entries.length + Math.min(bulkLineCount, maxEntries - entries.length)})
              </button>
              <button
                onClick={handleBulkReplace}
                disabled={bulkLineCount === 0}
                className="btn-bulk-action btn-secondary"
                data-testid="bulk-replace-button"
              >
                <Trash2 size={18} />
                {t('wheel.bulkImportReplace')} ({Math.min(bulkLineCount, maxEntries)})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EntryList;
